import {createAsyncThunk} from '@reduxjs/toolkit';

import {fetchEntityMetadata, IFieldMeta} from 'entities/metadata';
import {mapServerFieldMetaToAppFieldMeta} from 'modules/data/actions/field-data-actions';
import {fetchFormDataFields} from 'modules/data/data-api';
import {EEntityType} from 'shared/constants/entities';

import {
    TFetchRequestArgs,
    fetchRequestData,
    IGetActionStatusByIdAndActionCodeRequestArgs,
    getActionStatusByIdAndActionCodeRequest,
    TPostRequestArgs,
    postRequestCommentStatus,
    postRequestData,
    TPostRequestDataArgs,
    postRequestStatus,
    removeRequestData,
    IRunActionByIdAndActionCodeRequestArgs,
    runActionByIdAndActionCodeRequest,
    markRequestRead,
    updateDocJournal,
} from './request-slice-api';
import {REQUEST_SLICE} from './request-slice-constants';
import {IRequestData, ERequestStatus} from './request-slice-type';

export const loadRequestData = createAsyncThunk<
    IRequestData, TFetchRequestArgs
>(
    `${REQUEST_SLICE}/loadRequestData`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchRequestData(args);
            return response.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const readRequest = createAsyncThunk<
    string, {ids: string[]}
>(
    `${REQUEST_SLICE}/readRequest`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await markRequestRead(args.ids);
            return response.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateDocJournalRequest = createAsyncThunk<
    string, {requestId: string}
>(
    `${REQUEST_SLICE}/updateDocJournal`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await updateDocJournal(args);
            return response.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);
interface IRequestStatusesProps{
    commentId: string;
    requestFormKey: string;
}

interface IRequestStatusesData{
    statuses: ERequestStatus[];
    requestFormKey: string;
}
export const loadRequestStatusesData = createAsyncThunk<
    IRequestStatusesData, IRequestStatusesProps
>(
    `${REQUEST_SLICE}/loadRequestStatusesDate`,
    async ({requestFormKey, commentId}, {rejectWithValue}) => {
        try {
            const response = await postRequestStatus({commentId});
            return {
                statuses: response.data,
                requestFormKey,
            };
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);
export const loadRequestStatusesCommentData = createAsyncThunk<
    IRequestStatusesData, IRequestStatusesProps
>(
    `${REQUEST_SLICE}/loadRequestStatusesCommentData`,
    async ({requestFormKey, commentId}, {rejectWithValue}) => {
        try {
            const response = await postRequestCommentStatus({commentId});
            return {
                statuses: response.data,
                requestFormKey,
            };
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const removeRequest = createAsyncThunk<
    string, TPostRequestArgs
>(
    `${REQUEST_SLICE}/removeRequest`,
    async ({commentId}, {rejectWithValue}) => {
        try {
            const response = await removeRequestData({commentId});
            return response.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

interface IRequestFieldsMetadata{
    entityName: string;
    referenceUrl: string;
    id?: number;
}
export const loadRequestFieldsMetadata = createAsyncThunk<
  IFieldMeta[],
  IRequestFieldsMetadata
>(
    `${REQUEST_SLICE}/loadRequestFieldsMetadata`,
    async ({entityName, referenceUrl, id}, {rejectWithValue}) => {
        try {
            const response = await fetchEntityMetadata(entityName, EEntityType.FORM);
            const {data} = await fetchFormDataFields(referenceUrl, id);
            const metadata = response?.data;
            const additionalFields = metadata?.additionalOptions?.optionsField?.additionalFields || [];
            const newAdditionalFields = mapServerFieldMetaToAppFieldMeta(
                additionalFields,
                undefined,
                data,
            );
            return [...metadata?.fields, ...newAdditionalFields];
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);
export const saveRequestData = createAsyncThunk<IRequestStatusesData, TPostRequestDataArgs
>(
    `${REQUEST_SLICE}/loadRequestStatusesDate`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await postRequestData(args);
            return response?.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const getActionStatusByIdAndActionCode = createAsyncThunk<
    string, IGetActionStatusByIdAndActionCodeRequestArgs
    >(
        `${REQUEST_SLICE}/getActionStatusByIdAndActionCode`,
        async (args, {rejectWithValue}) => {
            try {
                const response = await getActionStatusByIdAndActionCodeRequest(args);
                return response.data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const runActionByIdAndActionCode = createAsyncThunk<
    string, IRunActionByIdAndActionCodeRequestArgs
    >(
        `${REQUEST_SLICE}/runActionByIdAndActionCode`,
        async (args, {rejectWithValue}) => {
            try {
                const response = await runActionByIdAndActionCodeRequest(args);
                return response.data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );
